import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { COUNTRIES, CountryCode, DEFAULT_COUNTRY, FALLBACK_RATES, getCountryConfig, isSupportedCountry } from './countries';
const COUNTRY_STORAGE_KEY = 'mpc.country';
const RATES_STORAGE_KEY = 'mpc.rates';
const RATES_TTL_MS = 24 * 60 * 60 * 1000;
const RATES_ENDPOINT = 'https://open.er-api.com/v6/latest/EUR';
const GEO_ENDPOINT = 'https://ipapi.co/json/';
type Rates = Record<string, number>;
type CachedRates = {
  timestamp: number;
  rates: Rates;
};
type CurrencyContextValue = {
  country: CountryCode;
  setCountry: (code: CountryCode) => void;
  formatPrice: (amountInEur: number) => string;
  buildCartUrl: (baseUrl: string) => string;
};
const CurrencyContext = createContext<CurrencyContextValue | null>(null);
export function CurrencyProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [country, setCountryState] = useState<CountryCode>(readStoredCountry);
  const [rates, setRates] = useState<Rates>(() => readCachedRates() ?? FALLBACK_RATES);
  useEffect(() => {
    let isCancelled = false;
    if (!hasStoredCountry()) {
      detectCountry().then(detected => {
        if (!isCancelled && detected) {
          setCountryState(detected);
        }
      });
    }
    if (!readCachedRates()) {
      fetchRates().then(fetched => {
        if (!isCancelled && fetched) {
          setRates(fetched);
        }
      });
    }
    return () => {
      isCancelled = true;
    };
  }, []);
  const setCountry = useCallback((code: CountryCode) => {
    setCountryState(code);
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, code);
    } catch {
      // Storage unavailable: keep the in-memory selection only.
    }
  }, []);
  const formatPrice = useCallback((amountInEur: number) => {
    const config = getCountryConfig(country);
    const rate = rates[config.currency] ?? FALLBACK_RATES[config.currency] ?? 1;
    const converted = amountInEur * rate;
    const rounded = config.decimals === 0 ? Math.round(converted) : Math.round(converted * 100) / 100;
    const formatted = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals
    }).format(rounded);
    return config.symbolAfter ? `${formatted} ${config.symbol}` : `${config.symbol}${formatted}`;
  }, [country, rates]);
  const buildCartUrl = useCallback((baseUrl: string) => {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}country=${country}`;
  }, [country]);
  const value = useMemo(() => ({
    country,
    setCountry,
    formatPrice,
    buildCartUrl
  }), [country, setCountry, formatPrice, buildCartUrl]);
  return <CurrencyContext.Provider value={value} data-id="element-99" data-mp="16b4y9pra2shz">{children}</CurrencyContext.Provider>;
}
export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used inside a CurrencyProvider');
  }
  return context;
}
export { COUNTRIES };
function readStoredCountry(): CountryCode {
  try {
    const stored = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (stored && isSupportedCountry(stored)) {
      return stored;
    }
  } catch {
    // Storage unavailable: fall back to the default country.
  }
  return DEFAULT_COUNTRY;
}
function hasStoredCountry(): boolean {
  try {
    const stored = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    return Boolean(stored && isSupportedCountry(stored));
  } catch {
    return false;
  }
}
function readCachedRates(): Rates | null {
  try {
    const stored = window.localStorage.getItem(RATES_STORAGE_KEY);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored) as CachedRates;
    if (!parsed?.rates || typeof parsed.timestamp !== 'number' || Date.now() - parsed.timestamp > RATES_TTL_MS) {
      return null;
    }
    return parsed.rates;
  } catch {
    return null;
  }
}
async function fetchRates(): Promise<Rates | null> {
  try {
    const response = await fetch(RATES_ENDPOINT);
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as {
      rates?: Rates;
    };
    if (!data.rates) {
      return null;
    }
    try {
      window.localStorage.setItem(RATES_STORAGE_KEY, JSON.stringify({
        timestamp: Date.now(),
        rates: data.rates
      }));
    } catch {
      // Storage unavailable: use the fetched rates for this session only.
    }
    return data.rates;
  } catch {
    return null;
  }
}
async function detectCountry(): Promise<CountryCode | null> {
  try {
    const response = await fetch(GEO_ENDPOINT);
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as {
      country_code?: string;
    };
    const code = data.country_code?.toUpperCase();
    if (code && isSupportedCountry(code)) {
      return code;
    }
    return null;
  } catch {
    return null;
  }
}